import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useIsUser, useIsManager, useIsAdmin } from 'hooks';
import { VALIDATION, APPLICATION_TYPE, WAGON_UTENSILS, STATUS_APPLICATION_WAGON } from 'helpers/constants';
import { Button, ButtonClose } from 'components/ui/form';
import { Input, Select, File } from 'components/ui/formik';
import History from '../history';
import Status from '../status';
import styles from '../../order.module.scss';

const SUBMIT_CLICK_DELAY = 1000;

const Form = ({
  edit,
  tons,
  wagons,
  stations,
  contracts,
  statusHistory,
  userConsignees,
  currentContractBalance,
  onSubmit,
  onChangeContract,
  onChangeType,
  onClose,
  getCountWagon,
  initialValues,
  getIndex,
  profile,
}) => {
  const isUser = useIsUser();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isManagement = isAdmin || isManager;
  const [status, setStatus] = useState(initialValues.status);
  const submitClickTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (submitClickTimeoutRef.current) {
      clearTimeout(submitClickTimeoutRef.current);
    }
  }, []);

  const handleCreateSubmit = (handleSubmit) => {
    if (submitClickTimeoutRef.current) {
      return;
    }

    handleSubmit();
    submitClickTimeoutRef.current = setTimeout(() => {
      submitClickTimeoutRef.current = null;
    }, SUBMIT_CLICK_DELAY);
  };

  const validationStatusSchema = () => status === STATUS_APPLICATION_WAGON.AGREED
    ? Yup.object().shape({
      gy12: Yup.string()
        .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
        .required(...VALIDATION.REQUIRED),
      statusComment: Yup.string()
        .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
        .required(...VALIDATION.REQUIRED),
    })
    : Yup.object().shape({
      statusComment: Yup.string()
        .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
        .required(...VALIDATION.REQUIRED),
    });

  const validationCreateSchema = Yup.object({
    codeGd: Yup.string()
      .min(...VALIDATION.MIN(4, VALIDATION.MIN_STRING))
      .max(...VALIDATION.MAX(4, VALIDATION.MAX_STRING))
      .required(...VALIDATION.REQUIRED),
  });

  const renderProductGroup = (values) => {
    if (tons && tons[values.contractId]) {
      const groupsArray = Object.keys(tons[values.contractId]);
      return groupsArray.length ? (
        <div className={[styles.item, styles.select].join(' ')}>
          <Select
            disabled={Boolean(edit)}
            label="Группа"
            name="productGroup"
          >
            {groupsArray.map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Select>
        </div>
      ) : null;
    }

    return null;
  };

  const renderTon = (values) => {
    if (tons && tons[values.contractId] && tons[values.contractId][values.productGroup]) {
      const contractIndex = getIndex(contracts, values.contractId);
      const { monthlyFund = true } = contractIndex >= 0 ? contracts[contractIndex] : {};
      const tonData = tons[values.contractId][values.productGroup];
      const tonMaxLimit = tonData[values.type];
      const isProductionCapacity = tonData.capacity && tonData.capacity[values.type] !== undefined
        ? tonMaxLimit > tonData.capacity[values.type]
        : false;
      const tonMax = isProductionCapacity ? tonData.capacity[values.type] : tonMaxLimit;

      return (
        <>
          <div className={[styles.item, styles.num].join(' ')}>
            <Input
              disabled={Boolean(edit)}
              label="Количество, тонн"
              type="number"
              name="ton"
              min={0}
              max={tonMax}
              step={1}
              onMax={() => {
                const fundMessage = monthlyFund
                  ? 'Превышен размер месячного фонда'
                  : 'Превышен размер суммы фондов';
                const message = isProductionCapacity ? 'Превышена производственная мощность' : fundMessage;
                const toastId = 'maxWagonTon';

                if (!toast.isActive(toastId)) {
                  toast.error(message, { toastId });
                }
              }}
            />
          </div>
          <div className={styles.item}>
            <Input disabled={Boolean(edit)} label="Количество вагонов" type="hidden" name="wagonCount" />
            <p className={styles.itemData}>{getCountWagon(values)}</p>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <>
      <div className={styles.heading}>
        <p className={styles.orderName}>{profile.name}</p>
        <p className={styles.orderWallet}>
          {currentContractBalance}
          {' '}
          руб
        </p>
      </div>
      <Formik
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={edit ? validationStatusSchema : validationCreateSchema}
      >
        {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
          const isChangedStatus = edit && edit.status !== Number(values.status);

          return (
            <>
              <form onSubmit={handleSubmit}>
                <div className={styles.orderContent}>
                  <div className={styles.orderWrap}>
                    {contracts.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select
                          disabled={Boolean(edit)}
                          label="Договор"
                          name="contractId"
                          onChange={e => onChangeContract(e, setFieldValue)}
                        >
                          {contracts.map(({ id, name, date }) => (
                            <option key={id} value={id}>
                              {name}
                              {' от '}
                              {moment(date).format('DD.MM.YYYY')}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {!isUser ? (
                      <>
                        {profile.uAddress ? (
                          <div className={styles.item}>
                            <p>
                              {profile.uAddress}
                              <br />
                              {`УНП ${profile.inn}`}
                            </p>
                          </div>
                        ) : null}
                        {(values.fio || values.email || values.phone) ? (
                          <p className={styles.orderTitle}>Исполнитель</p>
                        ) : null}
                        {values.fio && (
                          <div className={styles.item}>
                            <Input label="ФИО" placeholder="ФИО" name="fio" disabled />
                          </div>
                        )}
                        {values.email && (
                          <div className={styles.item}>
                            <Input label="Email" placeholder="Email" name="email" type="email" disabled />
                          </div>
                        )}
                        {values.phone && (
                          <div className={styles.item}>
                            <Input label="Телефон" placeholder="+375 (**) *** - ** - **" name="phone" type="tel" disabled />
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div className={styles.orderWrap}>
                    {stations.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled={Boolean(edit)} label="Станция назначения" name="stationId">
                          {stations.map(({ id, name, code }) => (
                            <option key={id} value={id}>{`${name} ${code ? `(${code})` : ''}`}</option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {renderProductGroup(values)}
                    {wagons.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled={Boolean(edit)} label="Вид вагона" name="wagonId">
                          {wagons.map(({ id, name, ton }) => (
                            <option key={id} value={id}>
                              {name}
                              {' '}
                              (
                              {ton}
                              {' '}
                              тонн)
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    <div className={styles.item}>
                      <Select disabled={Boolean(edit)} label="Тип заявки" name="type" onChange={e => onChangeType(e, setFieldValue)}>
                        {APPLICATION_TYPE.list().map(value => (
                          <option
                            key={value}
                            value={value}
                          >
                            {APPLICATION_TYPE.toString(value)}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {renderTon(values)}
                    {userConsignees.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled={Boolean(edit)} label="Грузополучатель" name="userConsigneeId">
                          <option key="default" value="default">Прочие (9999)</option>
                          {userConsignees.map(({ id, consignee: { name, codeGd } }) => (
                            <option key={id} value={id}>{`${name} ${codeGd ? `(${codeGd})` : ''}`}</option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    <div className={styles.item}>
                      <Input
                        disabled={Boolean(edit)}
                        label="ЖД код грузополучателя"
                        name="codeGd"
                        placeholder="ЖД код грузополучателя"
                      />
                    </div>
                    <div className={styles.item}>
                      <Select disabled={Boolean(edit)} label="Принадлежность вагонов" name="utensils">
                        {WAGON_UTENSILS.list().map(value => (
                          <option
                            key={value}
                            value={value}
                          >
                            {WAGON_UTENSILS.toString(value)}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {edit && isManagement ? (
                      <div className={styles.item}>
                        <Input disabled={!isChangedStatus} label="Номер ГУ-12" name="gy12" placeholder="Номер ГУ-12" />
                      </div>
                    ) : null}
                    {edit ? (
                      <Status
                        edit={edit}
                        values={values}
                        setStatus={setStatus}
                      />
                    ) : (
                      <div className={styles.item}>
                        <File
                          accept=".jpg, .jpeg, .png, .xlsx, .xls, .pdf"
                          name="file"
                          label="Приложить файл"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </form>
              {statusHistory.length ? (
                <div className={styles.history}>
                  <div className={styles.historyName}>История изменения статусов</div>
                  <div className={styles.historyTable}>
                    <History data={statusHistory} />
                  </div>
                </div>
              ) : null}
              <div className={styles.footer}>
                <div>
                  <ButtonClose onClick={() => onClose()}>{edit ? 'Закрыть' : 'Отменить'}</ButtonClose>
                </div>
                <div className={styles.buttons}>
                  {edit && isChangedStatus ? (
                    <Button
                      key={0}
                      onClick={handleSubmit}
                      type="submit"
                    >
                      Обновить
                    </Button>
                  ) : null}
                  {!edit ? (
                    <Button
                      key={1}
                      onClick={() => handleCreateSubmit(handleSubmit)}
                      type="button"
                      disabled={isSubmitting}
                    >
                      Оформить
                    </Button>
                  ) : null}
                </div>
              </div>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default Form;
